import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Loader2, Sparkles, Rocket, Globe, MapPin, Flag
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateImpactLab = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    problem_statement: '',
    challenge_type: 'regional',
    geographic_scope: ''
  });

  const challengeTypes = [
    { value: 'regional', label: 'Regional Impact', icon: MapPin },
    { value: 'national', label: 'National Impact', icon: Flag },
    { value: 'global', label: 'Global Impact', icon: Globe }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert('Please log in to start an Impact Lab');
      navigate('/auth');
      return;
    }

    try {
      setLoading(true);
      
      // Create challenge first (simplified for Impact Lab)
      const challengeResponse = await axios.post(
        `${BACKEND_URL}/api/challenges/create`,
        {
          title: formData.title,
          description: formData.problem_statement.substring(0, 200),
          full_description: formData.problem_statement,
          challenge_type: formData.challenge_type,
          geographic_scope: formData.geographic_scope,
          deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
          difficulty: 'intermediate',
          tags: ['Impact Lab', 'IDEO'],
          criteria: ['Innovation', 'Feasibility', 'Impact'],
          color: '#8B5CF6',
          is_impact_lab: true
        },
        { withCredentials: true }
      );
      
      if (challengeResponse.data.success) {
        const challengeId = challengeResponse.data.challenge_id;
        
        // Redirect to Impact Lab analysis
        navigate(`/challenges/${challengeId}/impact-lab`);
      }
    } catch (error) {
      console.error('Error creating Impact Lab:', error);
      alert(error.response?.data?.detail || 'Failed to create Impact Lab');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/challenges')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Start Impact Lab
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        {/* Intro Card */}
        <Card className="mb-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Rocket className="w-12 h-12 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-2">7-Day IDEO Design Thinking Sprint</h2>
                <p className="opacity-90">
                  Launch a structured innovation sprint using IDEO's proven methodology. 
                  Collaborate with 6-12 Fountainheads to solve real-world problems.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Problem Statement */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>What Problem Do You Want to Solve?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Challenge Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Reducing Plastic Waste in Coastal Cities"
                  required
                />
              </div>

              <div>
                <Label htmlFor="problem_statement">Problem Statement *</Label>
                <Textarea
                  id="problem_statement"
                  value={formData.problem_statement}
                  onChange={(e) => setFormData({ ...formData, problem_statement: e.target.value })}
                  placeholder="Describe the problem you want to tackle. Be specific about the context, stakeholders, and desired impact..."
                  rows={8}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The more detail you provide, the better the AI analysis will be
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Impact Scope */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Impact Scope</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Challenge Type</Label>
                <div className="grid md:grid-cols-3 gap-3 mt-2">
                  {challengeTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.challenge_type === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, challenge_type: type.value })}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`} />
                        <span className="text-sm font-semibold">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="geographic_scope">Geographic Scope</Label>
                <Input
                  id="geographic_scope"
                  value={formData.geographic_scope}
                  onChange={(e) => setFormData({ ...formData, geographic_scope: e.target.value })}
                  placeholder="e.g., Mumbai, India"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Specify location or leave blank for global</p>
              </div>
            </CardContent>
          </Card>

          {/* What Happens Next */}
          <Card className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Sparkles className="w-5 h-5" />
                What Happens Next
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3 text-sm text-gray-700">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span><strong>AI Analysis:</strong> GPT-5 analyzes your problem and generates insights, persona suggestions, and a 7-day IDEO roadmap</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span><strong>Build Your Team:</strong> Invite 6-12 Fountainheads with relevant expertise to join your sprint</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span><strong>7-Day Sprint:</strong> Follow IDEO's proven design thinking process with daily objectives and activities</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span><strong>Certificate:</strong> Receive a certificate of participation upon completion</span>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-lg py-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Impact Lab...
              </>
            ) : (
              <>
                <Rocket className="w-5 h-5 mr-2" />
                Launch Impact Lab Sprint
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateImpactLab;
